/**
 * @fileoverview Representative client-side dataset for the /entities workspace
 * (CONTEXT D-15). One case's worth of intelligence — case DR 26-00945 (the
 * armed-robbery case in MOCK_CASES) — shaped exactly to the ontology in
 * `entities-ontology.ts` so the later live-backend / OSDK swap is mechanical
 * (replace the queryFn in hooks.ts; delete these constants).
 *
 * Coverage (intentional, so the workspace reads as real):
 * - MOCK_ENTITIES spans ALL six IntelligenceEntityKind values, with a clear
 *   high-degree hub (the suspect, "Marcus L. Tatum").
 * - MOCK_RELATIONSHIPS covers all six RelationshipKind verbs and all three
 *   AssertionConfidence levels, forming a connected graph (every entity has at
 *   least one edge) so the deterministic layout has a real hub.
 * - MOCK_INGEST_SOURCES is a small set of representative source files (used by
 *   the ingest flow in 13-04).
 *
 * "Intelligence, not evidence" (D-06): every attribute `sourceCitation` points
 * at SOURCE MATERIAL (an interview page, a CDR export row, a Cellebrite report
 * line) — it never asserts verified evidentiary fact.
 */

import type {
  EntityRelationship,
  IntelligenceEntity,
  IntelligenceEntityKind,
} from "./entities-ontology";

const ORIGIN = "DR 26-00945";
const ANALYST = "Det. Castaneda #4471";

/**
 * Representative ingest source — a file submitted to the workspace whose
 * (simulated) extraction yields entities/relationships. Status mirrors the
 * UploadEvidenceDialog per-file state machine.
 */
export interface IngestSource {
  id: string;
  fileName: string;
  kind: "document" | "image" | "audio" | "video" | "cdr" | "device_extract";
  status: "queued" | "uploading" | "extracting" | "done" | "failed";
}

// -- Entities ---------------------------------------------------------------

export const MOCK_ENTITIES: IntelligenceEntity[] = [
  {
    id: "ent-marcus-tatum",
    kind: "person",
    canonicalIdentifier: "Marcus L. Tatum",
    displayName: "Marcus L. Tatum",
    originCaseReference: ORIGIN,
    attributes: [
      { name: "Role", value: "Suspect", sourceCitation: "Witness interview transcript p.3" },
      { name: "Date of birth", value: "1994-08-12 (28–32)", sourceCitation: "DMV record query log" },
      { name: "Known alias", value: '"Big M"', sourceCitation: "CDR export row 142" },
    ],
    registeredAt: "2026-02-13T04:10:00Z",
    lastReviewedAt: "2026-05-25T18:22:00Z",
  },
  {
    id: "ent-kyle-tatum",
    kind: "person",
    canonicalIdentifier: "Kyle D. Tatum",
    displayName: "Kyle D. Tatum",
    originCaseReference: ORIGIN,
    attributes: [
      { name: "Relationship", value: "Brother of suspect", sourceCitation: "Interview transcript p.7" },
      { name: "Role", value: "Associate", sourceCitation: "Cellebrite report — contacts p.12" },
    ],
    registeredAt: "2026-02-16T09:00:00Z",
    lastReviewedAt: "2026-05-20T11:05:00Z",
  },
  {
    id: "ent-jessica-martinez",
    kind: "person",
    canonicalIdentifier: "Jessica E. Martinez",
    displayName: "Jessica E. Martinez",
    originCaseReference: ORIGIN,
    attributes: [
      { name: "Role", value: "Witness (store clerk)", sourceCitation: "Witness interview transcript p.1" },
    ],
    registeredAt: "2026-02-15T11:30:00Z",
    lastReviewedAt: "2026-05-18T14:00:00Z",
  },
  {
    id: "ent-diana-pham",
    kind: "person",
    canonicalIdentifier: "Diana K. Pham",
    displayName: "Diana K. Pham",
    originCaseReference: ORIGIN,
    attributes: [
      { name: "Role", value: "Victim", sourceCitation: "Incident report narrative §2" },
    ],
    registeredAt: "2026-02-13T05:00:00Z",
    lastReviewedAt: "2026-05-10T09:00:00Z",
  },
  {
    id: "ent-unknown-passenger",
    kind: "person",
    canonicalIdentifier: "UNSUB — passenger seat",
    displayName: "Unidentified male — passenger",
    originCaseReference: ORIGIN,
    attributes: [
      { name: "Role", value: "Person of interest", sourceCitation: "Parking-lot CCTV frame 01:48:22" },
      { name: "Description", value: "Male, 25–40 (visual)", sourceCitation: "BWC review notes" },
    ],
    registeredAt: "2026-02-14T16:00:00Z",
    lastReviewedAt: "2026-05-02T10:00:00Z",
  },
  {
    id: "ent-phone-tatum",
    kind: "phone",
    canonicalIdentifier: "+1-702-555-0147",
    displayName: "+1 (702) 555-0147",
    originCaseReference: ORIGIN,
    attributes: [
      { name: "Carrier", value: "T-Mobile", sourceCitation: "Subscriber records SW-22517" },
      { name: "Subscriber", value: "M. Tatum (prepaid)", sourceCitation: "Subscriber records SW-22517" },
      { name: "IMEI", value: "35-XXXXX-XXXXX-3", sourceCitation: "Cellebrite extract — device info" },
    ],
    registeredAt: "2026-04-22T17:00:00Z",
    lastReviewedAt: "2026-05-12T13:00:00Z",
  },
  {
    id: "ent-phone-kyle",
    kind: "phone",
    canonicalIdentifier: "+1-702-555-0982",
    displayName: "+1 (702) 555-0982",
    originCaseReference: ORIGIN,
    attributes: [
      { name: "Carrier", value: "Verizon", sourceCitation: "CDR export header" },
      { name: "Contact frequency", value: "31 calls / 14 days", sourceCitation: "CDR export rows 142–173" },
    ],
    registeredAt: "2026-04-22T17:05:00Z",
    lastReviewedAt: "2026-05-12T13:05:00Z",
  },
  {
    id: "ent-phone-unknown",
    kind: "phone",
    canonicalIdentifier: "+1-702-555-0613",
    displayName: "+1 (702) 555-0613",
    originCaseReference: ORIGIN,
    attributes: [
      { name: "Status", value: "Unsubscribed / burner", sourceCitation: "CDR export row 201" },
    ],
    registeredAt: "2026-04-24T10:00:00Z",
    lastReviewedAt: "2026-04-30T10:00:00Z",
  },
  {
    id: "ent-vehicle-charger",
    kind: "vehicle",
    canonicalIdentifier: "NV 7KLM442",
    displayName: "Dodge Charger — NV 7KLM442",
    originCaseReference: ORIGIN,
    attributes: [
      { name: "Make / model", value: "2019 Dodge Charger, gray", sourceCitation: "Parking-lot CCTV frame 01:42:10" },
      { name: "Registered owner", value: "K. Tatum", sourceCitation: "DMV record query log" },
    ],
    registeredAt: "2026-02-14T16:30:00Z",
    lastReviewedAt: "2026-05-01T12:00:00Z",
  },
  {
    id: "ent-vehicle-civic",
    kind: "vehicle",
    canonicalIdentifier: "NV 9PQR018",
    displayName: "Honda Civic — NV 9PQR018",
    originCaseReference: ORIGIN,
    attributes: [
      { name: "Make / model", value: "2016 Honda Civic, white", sourceCitation: "City camera #114 frame 01:50:33" },
    ],
    registeredAt: "2026-02-18T09:00:00Z",
    lastReviewedAt: "2026-04-28T09:00:00Z",
  },
  {
    id: "ent-org-eastside",
    kind: "organization",
    canonicalIdentifier: "Eastside Holdings LLC",
    displayName: "Eastside Holdings LLC",
    originCaseReference: ORIGIN,
    attributes: [
      { name: "Type", value: "Shell company (suspected)", sourceCitation: "Subpoenaed bank records — Wells Fargo p.4" },
      { name: "Registered agent", value: "K. Tatum", sourceCitation: "Secretary of State filing query" },
    ],
    registeredAt: "2026-05-09T10:00:00Z",
    lastReviewedAt: "2026-05-22T10:00:00Z",
  },
  {
    id: "ent-org-crew",
    kind: "organization",
    canonicalIdentifier: "Tropicana crew",
    displayName: "Tropicana crew (informal)",
    originCaseReference: ORIGIN,
    attributes: [
      { name: "Type", value: "Associative group", sourceCitation: "Analyst link summary" },
    ],
    registeredAt: "2026-05-15T10:00:00Z",
    lastReviewedAt: "2026-05-24T10:00:00Z",
  },
  {
    id: "ent-loc-tropicana",
    kind: "location",
    canonicalIdentifier: "Tropicana & Mountain Vista",
    displayName: "Tropicana & Mountain Vista (scene)",
    originCaseReference: ORIGIN,
    attributes: [
      { name: "Type", value: "Robbery scene — convenience store", sourceCitation: "Incident report §1" },
    ],
    registeredAt: "2026-02-13T04:00:00Z",
    lastReviewedAt: "2026-05-01T08:00:00Z",
  },
  {
    id: "ent-loc-residence",
    kind: "location",
    canonicalIdentifier: "4412 Desert Bloom Ave",
    displayName: "4412 Desert Bloom Ave",
    originCaseReference: ORIGIN,
    attributes: [
      { name: "Type", value: "Suspect residence", sourceCitation: "DMV record query log" },
    ],
    registeredAt: "2026-02-17T09:00:00Z",
    lastReviewedAt: "2026-05-03T09:00:00Z",
  },
  {
    id: "ent-loc-storage",
    kind: "location",
    canonicalIdentifier: "Vault Self-Storage, Unit 214",
    displayName: "Vault Self-Storage — Unit 214",
    originCaseReference: ORIGIN,
    attributes: [
      { name: "Type", value: "Storage unit (rented)", sourceCitation: "Subpoenaed bank records — Wells Fargo p.6" },
    ],
    registeredAt: "2026-05-11T10:00:00Z",
    lastReviewedAt: "2026-05-20T10:00:00Z",
  },
  {
    id: "ent-account-checking",
    kind: "account",
    canonicalIdentifier: "WF ****4471",
    displayName: "Wells Fargo checking ****4471",
    originCaseReference: ORIGIN,
    attributes: [
      { name: "Holder", value: "M. Tatum", sourceCitation: "Subpoenaed bank records — Wells Fargo p.1" },
      { name: "Activity", value: "3 cash deposits > $4k", sourceCitation: "Subpoenaed bank records — Wells Fargo p.2" },
    ],
    registeredAt: "2026-05-09T11:00:00Z",
    lastReviewedAt: "2026-05-23T11:00:00Z",
  },
  {
    id: "ent-account-business",
    kind: "account",
    canonicalIdentifier: "WF ****8820",
    displayName: "Wells Fargo business ****8820",
    originCaseReference: ORIGIN,
    attributes: [
      { name: "Holder", value: "Eastside Holdings LLC", sourceCitation: "Subpoenaed bank records — Wells Fargo p.4" },
    ],
    registeredAt: "2026-05-09T11:05:00Z",
    lastReviewedAt: "2026-05-23T11:05:00Z",
  },
  {
    id: "ent-account-email",
    kind: "account",
    canonicalIdentifier: "marcus.tatum81@gmail.com",
    displayName: "marcus.tatum81@gmail.com",
    originCaseReference: ORIGIN,
    attributes: [
      { name: "Type", value: "Google account", sourceCitation: "Cellebrite extract — accounts p.3" },
    ],
    registeredAt: "2026-05-25T16:00:00Z",
    lastReviewedAt: "2026-05-25T16:30:00Z",
  },
  {
    id: "ent-person-ofc-park",
    kind: "person",
    canonicalIdentifier: "Officer R. Park #5512",
    displayName: "Officer R. Park #5512",
    originCaseReference: ORIGIN,
    attributes: [
      { name: "Role", value: "Responding officer", sourceCitation: "BWC metadata — Officer Park" },
    ],
    registeredAt: "2026-02-13T03:14:00Z",
    lastReviewedAt: "2026-04-15T09:00:00Z",
  },
  {
    id: "ent-phone-victim",
    kind: "phone",
    canonicalIdentifier: "+1-702-555-0301",
    displayName: "+1 (702) 555-0301",
    originCaseReference: ORIGIN,
    attributes: [
      { name: "Subscriber", value: "D. Pham", sourceCitation: "Victim statement form" },
    ],
    registeredAt: "2026-02-13T05:10:00Z",
    lastReviewedAt: "2026-04-10T09:00:00Z",
  },
];

// -- Relationships ----------------------------------------------------------
// Connected graph hub = ent-marcus-tatum. Covers all six relationshipKind
// verbs and all three confidence levels.

export const MOCK_RELATIONSHIPS: EntityRelationship[] = [
  {
    id: "rel-001",
    sourceEntityId: "ent-marcus-tatum",
    targetEntityId: "ent-kyle-tatum",
    relationshipKind: "associate_of",
    assertionConfidence: "confirmed",
    sourceReferenceCitation: "Interview transcript p.7",
    assertedBy: ANALYST,
    assertedAt: "2026-02-16T09:30:00Z",
  },
  {
    id: "rel-002",
    sourceEntityId: "ent-marcus-tatum",
    targetEntityId: "ent-phone-tatum",
    relationshipKind: "owns",
    assertionConfidence: "confirmed",
    sourceReferenceCitation: "Subscriber records SW-22517",
    assertedBy: ANALYST,
    assertedAt: "2026-04-22T17:10:00Z",
  },
  {
    id: "rel-003",
    sourceEntityId: "ent-phone-tatum",
    targetEntityId: "ent-phone-kyle",
    relationshipKind: "communicated_with",
    assertionConfidence: "confirmed",
    sourceReferenceCitation: "CDR export rows 142–173",
    assertedBy: ANALYST,
    assertedAt: "2026-05-12T13:30:00Z",
  },
  {
    id: "rel-004",
    sourceEntityId: "ent-phone-tatum",
    targetEntityId: "ent-phone-unknown",
    relationshipKind: "communicated_with",
    assertionConfidence: "probable",
    sourceReferenceCitation: "CDR export row 201",
    assertedBy: ANALYST,
    assertedAt: "2026-05-12T13:35:00Z",
  },
  {
    id: "rel-005",
    sourceEntityId: "ent-kyle-tatum",
    targetEntityId: "ent-phone-kyle",
    relationshipKind: "owns",
    assertionConfidence: "confirmed",
    sourceReferenceCitation: "Cellebrite report — contacts p.12",
    assertedBy: ANALYST,
    assertedAt: "2026-05-05T10:00:00Z",
  },
  {
    id: "rel-006",
    sourceEntityId: "ent-kyle-tatum",
    targetEntityId: "ent-vehicle-charger",
    relationshipKind: "owns",
    assertionConfidence: "confirmed",
    sourceReferenceCitation: "DMV record query log",
    assertedBy: ANALYST,
    assertedAt: "2026-02-14T16:45:00Z",
  },
  {
    id: "rel-007",
    sourceEntityId: "ent-marcus-tatum",
    targetEntityId: "ent-vehicle-charger",
    relationshipKind: "located_at",
    assertionConfidence: "probable",
    sourceReferenceCitation: "Parking-lot CCTV frame 01:42:10",
    assertedBy: ANALYST,
    assertedAt: "2026-02-14T17:00:00Z",
  },
  {
    id: "rel-008",
    sourceEntityId: "ent-marcus-tatum",
    targetEntityId: "ent-loc-tropicana",
    relationshipKind: "located_at",
    assertionConfidence: "probable",
    sourceReferenceCitation: "Tropicana convenience store — CCTV",
    assertedBy: ANALYST,
    assertedAt: "2026-02-14T18:00:00Z",
  },
  {
    id: "rel-009",
    sourceEntityId: "ent-diana-pham",
    targetEntityId: "ent-loc-tropicana",
    relationshipKind: "located_at",
    assertionConfidence: "confirmed",
    sourceReferenceCitation: "Incident report §2",
    assertedBy: ANALYST,
    assertedAt: "2026-02-13T05:30:00Z",
  },
  {
    id: "rel-010",
    sourceEntityId: "ent-jessica-martinez",
    targetEntityId: "ent-loc-tropicana",
    relationshipKind: "located_at",
    assertionConfidence: "confirmed",
    sourceReferenceCitation: "Witness interview transcript p.1",
    assertedBy: ANALYST,
    assertedAt: "2026-02-15T11:45:00Z",
  },
  {
    id: "rel-011",
    sourceEntityId: "ent-marcus-tatum",
    targetEntityId: "ent-loc-residence",
    relationshipKind: "located_at",
    assertionConfidence: "confirmed",
    sourceReferenceCitation: "DMV record query log",
    assertedBy: ANALYST,
    assertedAt: "2026-02-17T09:30:00Z",
  },
  {
    id: "rel-012",
    sourceEntityId: "ent-marcus-tatum",
    targetEntityId: "ent-org-crew",
    relationshipKind: "member_of",
    assertionConfidence: "possible",
    sourceReferenceCitation: "Analyst link summary",
    assertedBy: ANALYST,
    assertedAt: "2026-05-15T10:30:00Z",
  },
  {
    id: "rel-013",
    sourceEntityId: "ent-kyle-tatum",
    targetEntityId: "ent-org-crew",
    relationshipKind: "member_of",
    assertionConfidence: "possible",
    sourceReferenceCitation: "Analyst link summary",
    assertedBy: ANALYST,
    assertedAt: "2026-05-15T10:35:00Z",
  },
  {
    id: "rel-014",
    sourceEntityId: "ent-unknown-passenger",
    targetEntityId: "ent-org-crew",
    relationshipKind: "member_of",
    assertionConfidence: "possible",
    sourceReferenceCitation: "Parking-lot CCTV frame 01:48:22",
    assertedBy: ANALYST,
    assertedAt: "2026-05-16T10:00:00Z",
  },
  {
    id: "rel-015",
    sourceEntityId: "ent-kyle-tatum",
    targetEntityId: "ent-org-eastside",
    relationshipKind: "member_of",
    assertionConfidence: "confirmed",
    sourceReferenceCitation: "Secretary of State filing query",
    assertedBy: ANALYST,
    assertedAt: "2026-05-09T10:30:00Z",
  },
  {
    id: "rel-016",
    sourceEntityId: "ent-org-eastside",
    targetEntityId: "ent-account-business",
    relationshipKind: "owns",
    assertionConfidence: "confirmed",
    sourceReferenceCitation: "Subpoenaed bank records — Wells Fargo p.4",
    assertedBy: ANALYST,
    assertedAt: "2026-05-09T11:10:00Z",
  },
  {
    id: "rel-017",
    sourceEntityId: "ent-marcus-tatum",
    targetEntityId: "ent-account-checking",
    relationshipKind: "owns",
    assertionConfidence: "confirmed",
    sourceReferenceCitation: "Subpoenaed bank records — Wells Fargo p.1",
    assertedBy: ANALYST,
    assertedAt: "2026-05-09T11:15:00Z",
  },
  {
    id: "rel-018",
    sourceEntityId: "ent-account-checking",
    targetEntityId: "ent-account-business",
    relationshipKind: "linked_to",
    assertionConfidence: "probable",
    sourceReferenceCitation: "Subpoenaed bank records — Wells Fargo p.5 (transfer trace)",
    assertedBy: ANALYST,
    assertedAt: "2026-05-22T11:00:00Z",
  },
  {
    id: "rel-019",
    sourceEntityId: "ent-marcus-tatum",
    targetEntityId: "ent-account-email",
    relationshipKind: "owns",
    assertionConfidence: "probable",
    sourceReferenceCitation: "Cellebrite extract — accounts p.3",
    assertedBy: ANALYST,
    assertedAt: "2026-05-25T16:40:00Z",
  },
  {
    id: "rel-020",
    sourceEntityId: "ent-org-eastside",
    targetEntityId: "ent-loc-storage",
    relationshipKind: "owns",
    assertionConfidence: "probable",
    sourceReferenceCitation: "Subpoenaed bank records — Wells Fargo p.6",
    assertedBy: ANALYST,
    assertedAt: "2026-05-20T10:30:00Z",
  },
  {
    id: "rel-021",
    sourceEntityId: "ent-marcus-tatum",
    targetEntityId: "ent-unknown-passenger",
    relationshipKind: "associate_of",
    assertionConfidence: "possible",
    sourceReferenceCitation: "Parking-lot CCTV frame 01:48:22",
    assertedBy: ANALYST,
    assertedAt: "2026-05-02T10:30:00Z",
  },
  {
    id: "rel-022",
    sourceEntityId: "ent-unknown-passenger",
    targetEntityId: "ent-vehicle-civic",
    relationshipKind: "located_at",
    assertionConfidence: "possible",
    sourceReferenceCitation: "City camera #114 frame 01:50:33",
    assertedBy: ANALYST,
    assertedAt: "2026-05-02T10:35:00Z",
  },
  {
    id: "rel-023",
    sourceEntityId: "ent-marcus-tatum",
    targetEntityId: "ent-diana-pham",
    relationshipKind: "linked_to",
    assertionConfidence: "confirmed",
    sourceReferenceCitation: "Incident report narrative §2 (offense)",
    assertedBy: ANALYST,
    assertedAt: "2026-02-13T06:00:00Z",
  },
  {
    id: "rel-024",
    sourceEntityId: "ent-jessica-martinez",
    targetEntityId: "ent-marcus-tatum",
    relationshipKind: "linked_to",
    assertionConfidence: "probable",
    sourceReferenceCitation: "Witness interview transcript p.3 (identification)",
    assertedBy: ANALYST,
    assertedAt: "2026-02-15T12:00:00Z",
  },
  {
    id: "rel-025",
    sourceEntityId: "ent-person-ofc-park",
    targetEntityId: "ent-loc-tropicana",
    relationshipKind: "located_at",
    assertionConfidence: "confirmed",
    sourceReferenceCitation: "BWC metadata — Officer Park",
    assertedBy: ANALYST,
    assertedAt: "2026-02-13T03:14:00Z",
  },
  {
    id: "rel-026",
    sourceEntityId: "ent-diana-pham",
    targetEntityId: "ent-phone-victim",
    relationshipKind: "owns",
    assertionConfidence: "confirmed",
    sourceReferenceCitation: "Victim statement form",
    assertedBy: ANALYST,
    assertedAt: "2026-02-13T05:20:00Z",
  },
  {
    id: "rel-027",
    sourceEntityId: "ent-phone-unknown",
    targetEntityId: "ent-unknown-passenger",
    relationshipKind: "owns",
    assertionConfidence: "possible",
    sourceReferenceCitation: "CDR export row 201 (handset attribution)",
    assertedBy: ANALYST,
    assertedAt: "2026-05-03T10:00:00Z",
  },
];

// -- Ingest sources (used by the 13-04 ingest flow) -------------------------

export const MOCK_INGEST_SOURCES: IngestSource[] = [
  { id: "src-001", fileName: "tropicana-cctv-interior.mp4", kind: "video", status: "done" },
  { id: "src-002", fileName: "cellebrite-extract-SW-22409.ufdr", kind: "device_extract", status: "done" },
  { id: "src-003", fileName: "tmobile-cdr-SW-22517.csv", kind: "cdr", status: "done" },
  { id: "src-004", fileName: "wells-fargo-records.pdf", kind: "document", status: "extracting" },
  { id: "src-005", fileName: "witness-interview-martinez.m4a", kind: "audio", status: "queued" },
];

/** Convenience index used by the inspector + hooks (id -> entity). */
export const MOCK_ENTITIES_BY_ID: Record<string, IntelligenceEntity> = Object.fromEntries(
  MOCK_ENTITIES.map((e) => [e.id, e])
);

/** Count of entities per kind — handy for legend row counts. */
export function entityCountByKind(
  entities: IntelligenceEntity[]
): Record<IntelligenceEntityKind, number> {
  const out = {
    person: 0,
    phone: 0,
    vehicle: 0,
    organization: 0,
    location: 0,
    account: 0,
    drug: 0,
    license_plate: 0,
    money: 0,
  } satisfies Record<IntelligenceEntityKind, number>;
  for (const e of entities) out[e.kind] += 1;
  return out;
}
